import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PortalAdminLayout from "./PortalAdminLayout";
import "./PortalDashboard.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

function decodeToken(token) {
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
      .join(""),
  );
  return JSON.parse(jsonPayload);
}

const RefundGovernance = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [requests, setRequests] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pagination, setPagination] = useState({});
  const [refundPolicy, setRefundPolicy] = useState(null);
  const [baselineMetrics, setBaselineMetrics] = useState(null);
  const [filters, setFilters] = useState({
    status: "pending",
    governanceStatus: "all",
    page: 1,
    limit: 25,
  });
  const [decisionForm, setDecisionForm] = useState({
    decision: "approve",
    force: false,
    reason: "",
    notes: "",
  });

  const checkPortalAdmin = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return false;
    }

    try {
      const decoded = decodeToken(token);
      const hasPortalAdminAccess =
        decoded.role === "portal_admin" ||
        decoded.role === "R6" ||
        (decoded.roles &&
          (decoded.roles.includes("portal_admin") ||
            decoded.roles.includes("R6")));

      if (!hasPortalAdminAccess) {
        navigate("/portal-admin");
        return false;
      }

      return true;
    } catch (decodeErr) {
      navigate("/login");
      return false;
    }
  }, [navigate]);

  const tokenHeader = () => {
    const token = localStorage.getItem("ogestyle_token");
    return { Authorization: `Bearer ${token}` };
  };

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        status: filters.status,
        governanceStatus: filters.governanceStatus,
        page: String(filters.page),
        limit: String(filters.limit),
      });

      const response = await axios.get(
        `${API_BASE_URL}/api/admin/refund-requests?${params.toString()}`,
        {
          headers: tokenHeader(),
        },
      );

      setRequests(response.data.requests || []);
      setPagination(response.data.pagination || {});
      setRefundPolicy(response.data.refundPolicy || null);

      if (!selected && response.data.requests?.length > 0) {
        setSelected(response.data.requests[0]);
      }
    } catch (fetchErr) {
      setError(
        fetchErr.response?.data?.message ||
          "Unable to load refund governance queue",
      );
    } finally {
      setLoading(false);
    }
  }, [filters, selected]);

  const fetchDetail = useCallback(async (bookingId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/refund-requests/${bookingId}`,
        {
          headers: tokenHeader(),
        },
      );

      setSelected({
        ...response.data.refundRequest,
        governance: response.data.governanceCase,
        auditEvents: response.data.auditEvents || [],
      });
      setRefundPolicy(response.data.refundPolicy || null);
    } catch (fetchErr) {
      setError(
        fetchErr.response?.data?.message || "Unable to load refund details",
      );
    }
  }, []);

  const fetchBaselineMetrics = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/admin/baseline-metrics`,
        {
          headers: tokenHeader(),
        },
      );
      setBaselineMetrics(response.data.metrics || null);
    } catch (_) {
      setBaselineMetrics(null);
    }
  }, []);

  useEffect(() => {
    if (checkPortalAdmin()) {
      fetchQueue();
      fetchBaselineMetrics();
    }
  }, [checkPortalAdmin, fetchQueue, fetchBaselineMetrics]);

  const submitDecision = async () => {
    if (!selected?.bookingId) return;
    if (!decisionForm.reason.trim()) {
      setError("Decision reason is required.");
      return;
    }

    setSaving(true);
    setError("");

    try {
      await axios.post(
        `${API_BASE_URL}/api/admin/refund-requests/${selected.bookingId}/decision`,
        {
          decision: decisionForm.decision,
          force: decisionForm.force,
          reason: decisionForm.reason.trim(),
          notes: decisionForm.notes.trim(),
        },
        {
          headers: tokenHeader(),
        },
      );

      setDecisionForm((prev) => ({
        ...prev,
        reason: "",
        notes: "",
      }));

      await fetchDetail(selected.bookingId);
      await fetchQueue();
    } catch (submitErr) {
      setError(
        submitErr.response?.data?.message ||
          "Unable to save refund governance decision",
      );
    } finally {
      setSaving(false);
    }
  };

  const governanceStatusBadge = (status) => {
    const map = {
      pending_review: { color: "#d97706", label: "Pending Review" },
      approved: { color: "#2563eb", label: "Approved" },
      denied: { color: "#7c3aed", label: "Denied" },
      force_approved: { color: "#059669", label: "Force Approved" },
      force_denied: { color: "#dc2626", label: "Force Denied" },
    };

    const cfg = map[status] || { color: "#6b7280", label: status || "Unknown" };

    return (
      <span
        style={{
          padding: "3px 10px",
          borderRadius: "999px",
          border: `1px solid ${cfg.color}33`,
          background: `${cfg.color}14`,
          color: cfg.color,
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {cfg.label}
      </span>
    );
  };

  return (
    <PortalAdminLayout>
      <div className="portal-dashboard">
        <div className="dashboard-header">
          <h1>Refund Governance</h1>
          <p>Platform-admin queue and decision console for high-risk refund requests.</p>
        </div>

        {baselineMetrics && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "12px",
              marginBottom: "16px",
            }}
          >
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "10px 12px",
              }}
            >
              <div style={{ color: "#6b7280", fontSize: "12px" }}>
                Pre-Completion Eligible
              </div>
              <div style={{ fontSize: "20px", fontWeight: 700 }}>
                {Number(baselineMetrics.preCompletionEligible?.count || 0)}
              </div>
              <div style={{ color: "#6b7280", fontSize: "12px" }}>
                ${Number(baselineMetrics.preCompletionEligible?.amount || 0).toFixed(2)}
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "10px 12px",
              }}
            >
              <div style={{ color: "#6b7280", fontSize: "12px" }}>
                Refunds After Payout
              </div>
              <div style={{ fontSize: "20px", fontWeight: 700 }}>
                {Number(baselineMetrics.refundsAfterPayout?.count || 0)}
              </div>
              <div style={{ color: "#6b7280", fontSize: "12px" }}>
                ${Number(baselineMetrics.refundsAfterPayout?.amount || 0).toFixed(2)}
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "10px 12px",
              }}
            >
              <div style={{ color: "#6b7280", fontSize: "12px" }}>
                Instant Payout Outcomes
              </div>
              <div style={{ fontSize: "14px", fontWeight: 700 }}>
                Success: {Number(baselineMetrics.instantPayouts?.succeeded || 0)}
              </div>
              <div style={{ color: "#6b7280", fontSize: "12px" }}>
                Failed: {Number(baselineMetrics.instantPayouts?.failed || 0)} | Canceled: {Number(baselineMetrics.instantPayouts?.canceled || 0)}
              </div>
            </div>

            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "10px",
                padding: "10px 12px",
              }}
            >
              <div style={{ color: "#6b7280", fontSize: "12px" }}>
                Outstanding Liability
              </div>
              <div style={{ fontSize: "20px", fontWeight: 700 }}>
                ${Number(baselineMetrics.liability?.outstandingAmount || 0).toFixed(2)}
              </div>
              <div style={{ color: "#6b7280", fontSize: "12px" }}>
                Accounts: {Number(baselineMetrics.liability?.accounts || 0)}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div
            style={{
              marginBottom: "16px",
              padding: "10px 12px",
              borderRadius: "8px",
              border: "1px solid #fecaca",
              background: "#fff1f2",
              color: "#991b1b",
            }}
          >
            {error}
          </div>
        )}

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "360px 1fr",
            gap: "16px",
            alignItems: "start",
          }}
        >
          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "16px",
            }}
          >
            <div style={{ display: "grid", gap: "8px", marginBottom: "12px" }}>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, status: e.target.value, page: 1 }))
                }
              >
                <option value="pending">Refund Request: Pending</option>
                <option value="approved">Refund Request: Approved</option>
                <option value="denied">Refund Request: Denied</option>
                <option value="all">Refund Request: All</option>
              </select>
              <select
                value={filters.governanceStatus}
                onChange={(e) =>
                  setFilters((prev) => ({ ...prev, governanceStatus: e.target.value, page: 1 }))
                }
              >
                <option value="all">Governance: All</option>
                <option value="pending_review">Governance: Pending Review</option>
                <option value="approved">Governance: Approved</option>
                <option value="denied">Governance: Denied</option>
                <option value="force_approved">Governance: Force Approved</option>
                <option value="force_denied">Governance: Force Denied</option>
              </select>
              <button
                type="button"
                onClick={fetchQueue}
                style={{
                  padding: "8px 10px",
                  borderRadius: "8px",
                  border: "1px solid #d1d5db",
                  background: "#f9fafb",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Refresh Queue
              </button>
            </div>

            {loading ? (
              <div>Loading queue...</div>
            ) : (
              <div style={{ display: "grid", gap: "8px" }}>
                {requests.map((item) => {
                  const selectedId = selected?.bookingId || selected?._id;
                  const isActive = String(selectedId) === String(item.bookingId);
                  return (
                    <button
                      key={item.bookingId}
                      type="button"
                      onClick={() => fetchDetail(item.bookingId)}
                      style={{
                        textAlign: "left",
                        padding: "10px",
                        borderRadius: "10px",
                        border: isActive
                          ? "1px solid #2563eb"
                          : "1px solid #e5e7eb",
                        background: isActive ? "#eff6ff" : "white",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ fontWeight: 700 }}>{item.bookingNumber}</div>
                      <div style={{ fontSize: "12px", color: "#4b5563" }}>
                        {item.customer?.email || "Unknown customer"}
                      </div>
                      <div style={{ marginTop: "6px" }}>
                        {governanceStatusBadge(item.governance?.status)}
                      </div>
                    </button>
                  );
                })}
                {requests.length === 0 && (
                  <div style={{ color: "#6b7280", fontSize: "14px" }}>
                    No refund requests match these filters.
                  </div>
                )}
              </div>
            )}

            <div style={{ marginTop: "10px", display: "flex", alignItems: "center", gap: "8px", fontSize: "12px", color: "#6b7280" }}>
              <button
                type="button"
                disabled={filters.page <= 1}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page - 1 }))}
                style={{
                  padding: "4px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  background: filters.page <= 1 ? "#f3f4f6" : "white",
                  cursor: filters.page <= 1 ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  opacity: filters.page <= 1 ? 0.5 : 1,
                }}
              >
                &#8249; Prev
              </button>
              <span>
                Page {pagination.page || 1} of {pagination.totalPages || 1} &nbsp;|&nbsp; {pagination.total || 0} total
              </span>
              <button
                type="button"
                disabled={filters.page >= (pagination.totalPages || 1)}
                onClick={() => setFilters((prev) => ({ ...prev, page: prev.page + 1 }))}
                style={{
                  padding: "4px 10px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  background: filters.page >= (pagination.totalPages || 1) ? "#f3f4f6" : "white",
                  cursor: filters.page >= (pagination.totalPages || 1) ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  opacity: filters.page >= (pagination.totalPages || 1) ? 0.5 : 1,
                }}
              >
                Next &#8250;
              </button>
            </div>
          </div>

          <div
            style={{
              background: "white",
              border: "1px solid #e5e7eb",
              borderRadius: "12px",
              padding: "16px",
            }}
          >
            {!selected ? (
              <div>Select a refund request to view details.</div>
            ) : (
              <>
                <div style={{ display: "flex", justifyContent: "space-between", gap: "12px" }}>
                  <div>
                    <h3 style={{ margin: 0 }}>{selected.bookingNumber}</h3>
                    <p style={{ margin: "6px 0 0", color: "#6b7280" }}>
                      {selected.customer?.email || "Unknown customer"}
                    </p>
                  </div>
                  <div>{governanceStatusBadge(selected.governance?.status)}</div>
                </div>

                <div style={{ marginTop: "12px", fontSize: "14px", color: "#374151" }}>
                  <div>Requested refund amount: ${Number(selected.request?.requestedRefundAmount || selected.totalBookingAmount || 0).toFixed(2)}</div>
                  <div>Policy threshold: ${Number(refundPolicy?.adminApprovalThresholdAmount || 0).toFixed(2)}</div>
                  <div>Risk: {selected.riskFlags?.requiresAdminApproval ? "Admin approval required" : "No mandatory gate"}</div>
                </div>

                <div
                  style={{
                    marginTop: "14px",
                    borderTop: "1px solid #e5e7eb",
                    paddingTop: "14px",
                    display: "grid",
                    gap: "10px",
                  }}
                >
                  <h4 style={{ margin: 0 }}>Record Governance Decision</h4>

                  <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                    <label>
                      <input
                        type="radio"
                        name="decision"
                        checked={decisionForm.decision === "approve"}
                        onChange={() =>
                          setDecisionForm((prev) => ({ ...prev, decision: "approve" }))
                        }
                      />{" "}
                      Approve
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="decision"
                        checked={decisionForm.decision === "deny"}
                        onChange={() =>
                          setDecisionForm((prev) => ({ ...prev, decision: "deny" }))
                        }
                      />{" "}
                      Deny
                    </label>
                    <label>
                      <input
                        type="checkbox"
                        checked={decisionForm.force}
                        onChange={(e) =>
                          setDecisionForm((prev) => ({ ...prev, force: e.target.checked }))
                        }
                      />{" "}
                      Force override
                    </label>
                  </div>

                  <textarea
                    value={decisionForm.reason}
                    onChange={(e) =>
                      setDecisionForm((prev) => ({ ...prev, reason: e.target.value }))
                    }
                    placeholder="Required decision reason"
                    rows={3}
                    style={{ width: "100%" }}
                  />

                  <textarea
                    value={decisionForm.notes}
                    onChange={(e) =>
                      setDecisionForm((prev) => ({ ...prev, notes: e.target.value }))
                    }
                    placeholder="Optional notes"
                    rows={2}
                    style={{ width: "100%" }}
                  />

                  <button
                    type="button"
                    onClick={submitDecision}
                    disabled={saving}
                    style={{
                      width: "fit-content",
                      padding: "10px 14px",
                      borderRadius: "8px",
                      border: "none",
                      background: "#2563eb",
                      color: "white",
                      fontWeight: 700,
                      cursor: saving ? "not-allowed" : "pointer",
                      opacity: saving ? 0.6 : 1,
                    }}
                  >
                    {saving ? "Saving..." : "Save Decision"}
                  </button>

                  <p style={{ margin: 0, color: "#6b7280", fontSize: "12px" }}>
                    Governance decisions are logged to immutable audit events. This phase records approval state and does not execute payment operations.
                  </p>
                </div>

                <div style={{ marginTop: "16px" }}>
                  <h4 style={{ margin: "0 0 8px" }}>Recent Governance Audit</h4>
                  <div style={{ display: "grid", gap: "8px" }}>
                    {(selected.auditEvents || []).slice(0, 10).map((evt) => (
                      <div
                        key={evt._id || `${evt.actionType}-${evt.createdAt}`}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: "8px",
                          padding: "8px 10px",
                          fontSize: "13px",
                        }}
                      >
                        <div style={{ fontWeight: 700 }}>{evt.actionType}</div>
                        <div style={{ color: "#6b7280" }}>
                          {new Date(evt.createdAt).toLocaleString()}
                        </div>
                        <div style={{ marginTop: "4px" }}>{evt.reason}</div>
                      </div>
                    ))}
                    {(selected.auditEvents || []).length === 0 && (
                      <div style={{ color: "#6b7280", fontSize: "14px" }}>
                        No governance decisions recorded yet.
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </PortalAdminLayout>
  );
};

export default RefundGovernance;
